import { Box } from '@mui/material';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { H2, H6 } from '../typography';
import { FormValues } from './CreateStep1V3';
import { isEmailValid } from '../../utils/validations';

// Import sektioner som flyttats från steg 1
import { ApartmentInfoSection } from './sections/ApartmentInfoSection';
import { ContractPeriodSection } from './sections/ContractPeriodSection';
import { EconomicSection } from './sections/EconomicSection';
import { IncludedServicesSection } from './sections/IncludedServicesSection';
import { MiscellaneousSection } from './sections/MiscellaneousSection';
import { FormSection } from '../form/FormSection';
import { ResponsiveInput } from '../form/ResponsiveInput';
import { FORM_STYLES } from '../../styles/formStyles';
import { Person } from '@mui/icons-material';

type CreateStep2Props = {
  values: FormValues;
  handleChange: (
    name: string
  ) => (value: React.ChangeEvent<HTMLInputElement>) => void;
  onValidationChange?: (hasErrors: boolean, triggerValidation?: () => boolean) => void;
  // För avtalsperiod-validering (hämtas från DB)
  rentalPeriodStart?: string;
  rentalPeriodEnd?: string;
  rentalPeriodLoading?: boolean;
};

interface ValidationErrors {
  tenantFirstName?: string;
  tenantLastName?: string;
  tenantPhoneNumber?: string;
  tenantPersonalNumber?: string;
  tenantEmail?: string;
  tenantEmailConfirm?: string;
  // Ekonomiska fält
  rentalFee?: string;
  deposit?: string;
  clearingnummer?: string;
  bankAccountNumber?: string;
  // Index signature för kompatibilitet med Record<string, string | undefined>
  [key: string]: string | undefined;
}

export const CreateStep2: React.FC<CreateStep2Props> = ({
  values,
  handleChange,
  onValidationChange,
  rentalPeriodStart,
  rentalPeriodEnd,
  rentalPeriodLoading = false
}) => {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Validation functions
  const validateField = (fieldName: string, value: string): string | undefined => {
    switch (fieldName) {
      case 'tenantFirstName':
        if (!value.trim()) return 'Förnamn är obligatoriskt';
        if (value.trim().length < 2) return 'Förnamn måste vara minst 2 tecken';
        break;
      
      case 'tenantLastName':
        if (!value.trim()) return 'Efternamn är obligatoriskt';
        if (value.trim().length < 2) return 'Efternamn måste vara minst 2 tecken';
        break;
      
      case 'tenantPhoneNumber':
        if (!value.trim()) return 'Telefonnummer är obligatoriskt';
        const phoneNumbers = value.replace(/[\s-+]/g, ''); // Ta bort mellanslag, bindestreck och plus
        if (!/^[0-9]+$/.test(phoneNumbers)) return 'Telefonnummer får endast innehålla siffror';
        break;
      
      case 'tenantPersonalNumber':
        if (!value.trim()) return 'Personnummer är obligatoriskt';

        // Ta bort mellanslag och bindestreck för validering
        const cleaned = value.replace(/[\s-]/g, '');

        // Kolla format: 12 siffror (8 datum + 4 sista)
        if (!/^\d{12}$/.test(cleaned)) {
          return 'Format: YYYYMMDD-XXXX (svenskt) eller YYYYMMDD-0000 (utländsk medborgare)';
        }

        // Validera datum-delen (första 8 siffror)
        const year = parseInt(cleaned.substring(0, 4));
        const month = parseInt(cleaned.substring(4, 6));
        const day = parseInt(cleaned.substring(6, 8));

        if (year < 1900 || year > new Date().getFullYear()) {
          return 'Ogiltigt födelseår';
        }
        if (month < 1 || month > 12) {
          return 'Ogiltig födelsemånad';
        }
        if (day < 1 || day > 31) {
          return 'Ogiltig födelsedag';
        }

        // De sista 4 siffrorna måste vara antingen fyrsiffrigt nummer eller 0000
        const lastFour = cleaned.slice(8);
        if (!/^\d{4}$/.test(lastFour)) {
          return 'Sista fyra tecknen måste vara siffror';
        }

        break;
      
      case 'tenantEmail':
        if (!value.trim()) return 'E-postadress är obligatorisk';
        if (!isEmailValid(value)) return 'Ange en giltig e-postadress';
        break;
      
      case 'tenantEmailConfirm':
        if (!value.trim()) return 'Bekräfta e-postadress';
        if (value !== values.tenantEmail) return 'E-postadresserna stämmer inte överens';
        break;

      // Ekonomiska fält
      case 'rentalFee':
        if (value === null || value === undefined || value === '') return 'Hyresavgift är obligatoriskt';
        if (Number(value) <= 0) return 'Hyresavgift måste vara större än 0';
        if (Number(value) < 1000) return 'Hyresavgift verkar vara för låg (under 1000 kr)';
        break;

      case 'deposit':
        if (value === null || value === undefined || value === '') return 'Ange deposition (kan vara 0 kr)';
        if (Number(value) < 0) return 'Deposition kan inte vara negativt';
        break;

      case 'clearingnummer':
        if (!value?.toString().trim()) return 'Clearingnummer är obligatoriskt';
        break;

      case 'bankAccountNumber':
        if (!value?.toString().trim()) return 'Kontonummer är obligatoriskt';
        break;

      default:
        return undefined;
    }
    return undefined;
  };

  // Validate all fields and update errors
  const validateAllFields = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    // Validate each field
    const fieldsToValidate = [
      'tenantFirstName', 'tenantLastName', 'tenantPhoneNumber',
      'tenantPersonalNumber', 'tenantEmail', 'tenantEmailConfirm',
      // Ekonomiska fält
      'rentalFee', 'deposit', 'clearingnummer', 'bankAccountNumber'
    ];
    
    fieldsToValidate.forEach(field => {
      const value = values[field as keyof FormValues] as string;
      const error = validateField(field, value || '');
      if (error) {
        newErrors[field as keyof ValidationErrors] = error;
      }
    });
    
    return newErrors;
  };

  // Function to trigger validation (called from parent when attempting to submit)
  const triggerValidation = useCallback(() => {
    setHasAttemptedSubmit(true);
    const newErrors = validateAllFields();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [values]);

  // Memoize validation errors to avoid recalculating on every render
  const currentErrors = useMemo(() => validateAllFields(), [
    values.tenantFirstName,
    values.tenantLastName,
    values.tenantPhoneNumber,
    values.tenantPersonalNumber,
    values.tenantEmail,
    values.tenantEmailConfirm,
    // Ekonomiska fält
    values.rentalFee,
    values.deposit,
    values.clearingnummer,
    values.bankAccountNumber
  ]);
  
  const hasErrors = Object.keys(currentErrors).length > 0;
  
  
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasErrors, triggerValidation);
    }
  }, [hasErrors, triggerValidation, onValidationChange]);

  // Only validate fields that have been touched or if submit was attempted
  useEffect(() => {
    if (hasAttemptedSubmit) {
      const newErrors = validateAllFields();
      setErrors(newErrors);
    }
  }, [values, hasAttemptedSubmit]);

  // Enhanced handleChange with validation
  const handleFieldChange = (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    // Call original handleChange
    handleChange(fieldName)(event);

    // Clear error for this field when user starts typing
    if (errors[fieldName as keyof ValidationErrors]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: undefined
      }));
    }
  };

  // Handle blur - markera fält som touched
  const handleBlur = (fieldName: string) => () => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  // Hjälpfunktion för att avgöra om fel ska visas
  const shouldShowError = (fieldName: string): boolean => {
    return hasAttemptedSubmit || touchedFields.has(fieldName);
  };

  // Adapter för ResponsiveInput som förväntar sig bara värdet
  const handleResponsiveInputChange = (fieldName: string) => (value: any) => {
    let formattedValue = value;

    // Auto-formatering för personnummer
    if (fieldName === 'tenantPersonalNumber' && value) {
      // Ta bort allt som inte är siffror
      const cleaned = value.replace(/\D/g, '');

      // Lägg till bindestreck automatiskt efter 8 siffror
      if (cleaned.length > 8) {
        formattedValue = cleaned.slice(0, 8) + '-' + cleaned.slice(8, 12);
      } else {
        formattedValue = cleaned;
      }
    }

    // Skapa mock event för handleFieldChange
    const mockEvent = {
      target: { value: formattedValue }
    } as React.ChangeEvent<HTMLInputElement>;

    handleFieldChange(fieldName)(mockEvent);
  };
  return (
    <>
      <H2 fontWeight="bold">Hyresgäst & Ekonomiska villkor</H2>
      <H6 sx={{ mt: 1 }}>Fyll i hyresgästinformation och ekonomiska villkor för uthyrningen.</H6>
      
      {/* Show validation summary if there are errors and user has touched fields or attempted submit */}
      {Object.keys(currentErrors).length > 0 && (
        hasAttemptedSubmit || Array.from(touchedFields).some(f => currentErrors[f as keyof ValidationErrors])
      ) && (
        <Box sx={{
          mt: 2,
          p: 2,
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 1,
          mb: 2
        }}>
          <H6 sx={{ color: '#dc2626', fontWeight: 'bold', mb: 1 }}>
            Kontrollera följande fält:
          </H6>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#dc2626' }}>
            {Object.entries(currentErrors).map(([field, error]) => (
              <li key={field} style={{ fontSize: '14px', marginBottom: '4px' }}>
                {error}
              </li>
            ))}
          </ul>
        </Box>
      )}

      {/* HYRESGÄSTINFORMATION SEKTION */}
      <FormSection
        icon={Person}
        iconColor="#62B8E9"
        title="tenant_information"
      >
        <Box sx={FORM_STYLES.layout.twoColumn}>
          <ResponsiveInput
            label="Förnamn"
            value={values.tenantFirstName}
            onChange={handleResponsiveInputChange('tenantFirstName')}
            onBlur={handleBlur('tenantFirstName')}
            type="text"
            placeholder="Anna"
            required={true}
            error={shouldShowError('tenantFirstName') && !!currentErrors.tenantFirstName}
            helperText={shouldShowError('tenantFirstName') ? currentErrors.tenantFirstName : ''}
            autoComplete="given-name"
          />

          <ResponsiveInput
            label="Efternamn"
            value={values.tenantLastName}
            onChange={handleResponsiveInputChange('tenantLastName')}
            onBlur={handleBlur('tenantLastName')}
            type="text"
            placeholder="Andersson"
            required={true}
            error={shouldShowError('tenantLastName') && !!currentErrors.tenantLastName}
            helperText={shouldShowError('tenantLastName') ? currentErrors.tenantLastName : ''}
            autoComplete="family-name"
          />
        </Box>

        <Box sx={{ ...FORM_STYLES.layout.twoColumn, mt: 3 }}>
          <ResponsiveInput
            label="Telefonnummer"
            value={values.tenantPhoneNumber}
            onChange={handleResponsiveInputChange('tenantPhoneNumber')}
            onBlur={handleBlur('tenantPhoneNumber')}
            type="text"
            placeholder="070-123 45 67"
            required={true}
            error={shouldShowError('tenantPhoneNumber') && !!currentErrors.tenantPhoneNumber}
            helperText={shouldShowError('tenantPhoneNumber') ? currentErrors.tenantPhoneNumber : ''}
            autoComplete="tel"
          />

          <ResponsiveInput
            label="Personnummer"
            value={values.tenantPersonalNumber}
            onChange={handleResponsiveInputChange('tenantPersonalNumber')}
            onBlur={handleBlur('tenantPersonalNumber')}
            type="text"
            placeholder="YYYYMMDD-XXXX"
            required={true}
            error={shouldShowError('tenantPersonalNumber') && !!currentErrors.tenantPersonalNumber}
            helperText={shouldShowError('tenantPersonalNumber') ? currentErrors.tenantPersonalNumber : ''}
            infoText="Svenskt personnummer: YYYYMMDD-XXXX (t.ex. 19900515-1234). Utländsk medborgare: YYYYMMDD-0000 (t.ex. 19900515-0000)"
            autoComplete="off"
          />
        </Box>

        <Box sx={{ ...FORM_STYLES.layout.twoColumn, mt: 3 }}>
          <ResponsiveInput
            label="E-postadress"
            value={values.tenantEmail}
            onChange={handleResponsiveInputChange('tenantEmail')}
            onBlur={handleBlur('tenantEmail')}
            type="email"
            placeholder="anna@example.com"
            required={true}
            error={shouldShowError('tenantEmail') && !!currentErrors.tenantEmail}
            helperText={shouldShowError('tenantEmail') ? currentErrors.tenantEmail : ''}
            autoComplete="email"
          />

          <ResponsiveInput
            label="Bekräfta e-postadress"
            value={values.tenantEmailConfirm}
            onChange={handleResponsiveInputChange('tenantEmailConfirm')}
            onBlur={handleBlur('tenantEmailConfirm')}
            type="email"
            placeholder="anna@example.com"
            required={true}
            error={shouldShowError('tenantEmailConfirm') && !!currentErrors.tenantEmailConfirm}
            helperText={shouldShowError('tenantEmailConfirm') ? currentErrors.tenantEmailConfirm : ''}
            autoComplete="off"
          />
        </Box>
      </FormSection>

      {/* LÄGENHETSINFORMATION (flyttad från steg 1) */}
      <ApartmentInfoSection
        values={values}
        handleChange={handleChange}
      />

      {/* AVTALSPERIOD (NYTT - inom uthyrningsperioden från steg 1) */}
      <ContractPeriodSection
        values={values}
        handleChange={handleChange}
        rentalPeriodStart={rentalPeriodStart}
        rentalPeriodEnd={rentalPeriodEnd}
        loading={rentalPeriodLoading}
      />

      {/* EKONOMISKA SEKTIONER (flyttade från steg 1) */}
      <EconomicSection
        values={values}
        handleChange={handleChange}
        errors={currentErrors}
        onBlur={handleBlur}
        shouldShowError={shouldShowError}
      />

      <IncludedServicesSection
        values={values}
        handleChange={handleChange}
      />

      <MiscellaneousSection
        values={values}
        handleChange={handleChange}
        onBlur={handleBlur}
      />
    </>
  );
};
